import { CommonModule, isPlatformBrowser } from '@angular/common';
import { Component, ElementRef, EventEmitter, HostListener, Inject, Input, Output, PLATFORM_ID, ViewChild } from '@angular/core';

@Component({
  selector: 'app-modal',
  imports: [CommonModule],
  templateUrl: './modal.html',
  // Renders nothing when closed and a `fixed inset-0` overlay when open, so the
  // host must not generate a box — otherwise it is an empty grid item (a gap)
  // wherever a modal sits inside a grid, e.g. the "All" page's shared grid.
  styles: [':host { display: contents; }'],
})
export class Modal {
  @Input() isOpen = false;
  @Output() close = new EventEmitter<void>();
  @Input() className = '';
  @Input() showCloseButton = true;
  @Input() isFullscreen = false;

  // The host itself is `display: contents` (see the `styles` note above), so
  // it renders no box of its own — `getBoundingClientRect()` on `this.el`
  // would return an empty rect. Content bound inside this modal that needs to
  // stay visually within the card (the caption emoji picker, notably) has to
  // reach the real content box instead.
  @ViewChild('contentBox') private contentBoxRef?: ElementRef<HTMLElement>;
  get contentElement(): HTMLElement | undefined {
    return this.contentBoxRef?.nativeElement;
  }

  constructor(private el: ElementRef, @Inject(PLATFORM_ID) private platformId: Object) { }

  ngOnInit() {
    if (this.isOpen) {
      if (isPlatformBrowser(this.platformId)) {
        document.body.style.overflow = 'hidden';
      }
    }
  }

  ngOnDestroy() {
    if (isPlatformBrowser(this.platformId)) {
      document.body.style.overflow = 'unset';
    }
  }

  ngOnChanges() {
    if (isPlatformBrowser(this.platformId)) {
      document.body.style.overflow = this.isOpen ? 'hidden' : 'unset';
    }
  }

  onBackdropClick(event: MouseEvent) {
    if (!this.isFullscreen) {
      this.close.emit();
    }
  }

  @HostListener('document:keydown.escape')
  onEscape() {
    if (this.isOpen) {
      this.close.emit();
    }
  }
}
